import React, {useEffect, useState} from "react";
import {Badge, Col, Modal, Row, Spinner} from "react-bootstrap";
import Button from "react-bootstrap/esm/Button";
import {MdOutlineCancel} from "react-icons/md";
import {TbReload} from "react-icons/tb";
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import useInstallationOptions from "../../../../../hooks/useInstallationOptions";
import {getGreekCurrencyFormat, isEmpty, quickClone} from "../../../../../_helpers/commonFunctions";
import Alert from "react-bootstrap/esm/Alert";
import Dropdown from "../../../../primer/Dropdown";
import {preNumberGreek} from "../../../../../_data/settings/advanced-types-search";
import EditableTable from "../../../../primer/Editable-Table";
import {fetchPOS} from "../../../../../_apis/api";
import {toast} from "react-toastify";

const AIPrepaidStatementModal = ({show, setShow, aiDoc, onSave, onReprocess, onDecline}) => {
    const dispatch = useDispatch();
    const aiSettings = useSelector((state) => state.AI.aiSettings);
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const allDocumentTypes = useSelector((state) => state.SETTINGS.settings.advancedTypes);
    const bankAccounts = useSelector((state) => state.CASH_DATA.bankAccounts);
    const bankAccountOptions = bankAccounts.map((o) => ({value: o._id, label: o.name}));
    const buysGeneralSettings = useSelector((state) => state.BUYS_SETTINGS.buysGeneralSettings);
    const buysPaymentTypes = (buysGeneralSettings.paymentTypes?.filter((el) => !["cheque", "credit"].includes(el.type)) || [])
        .map((o) => ({value: o._id, label: o.name}));
    const cardList = useSelector((state) => state.CASH_DATA.cardList);
    const cardOptions = cardList.map((o) => ({value: o._id, label: o.name}));

    const [cardIsLocked, setCardIsLocked] = useState(false);
    const [prepaidObject, setPrepaidObject] = useState(null);
    const [entitiesList, setEntitiesList] = useState([]);

    const [loadingSave, setLoadingSave] = useState(false);
    const [incrKey, setIncrKey] = useState(0);
    const [incrKey2, setIncrKey2] = useState(0);

    const installationOptions = useInstallationOptions(false);
    const documentTypeOptions = allDocumentTypes.filter((el) => el.section === "Supplier Payments")
        .map((o) => ({value: o._id, label: o.name}));

    const selectedPaymentTypeType = prepaidObject?.paymentType ?
        buysGeneralSettings.paymentTypes.find((el) => el._id === prepaidObject.paymentType)?.type : null;

    useEffect(() => {
        if (show) {
            dispatch(fetchPOS({company: company.id}));
            setPrepaidObject({
                card: "",
                paymentType: aiSettings?.prepaidStatementsDefaultPaymentType || "",
                ...aiDoc.prepaidStatement,
                bankAccount: "",
                installation: "0",
                documentType: aiSettings?.prepaidStatementsDefaultSP || "",
                preNumber: aiSettings?.prepaidStatementsDefaultSPPreNumber || "",
                documentTypeReturn: aiSettings?.prepaidStatementsDefaultSPReturn || "",
                preNumberReturn: aiSettings?.prepaidStatementsDefaultSPReturnPreNumber || "",
            });
            loadEntitiesList(null, setEntitiesList);
            setCardIsLocked(!!aiDoc.prepaidStatement?.card && !!aiDoc.prepaidStatement?.paymentType);
        } else {
            setCardIsLocked(false);
            setPrepaidObject(null);
            setEntitiesList([]);
        }
    }, [show])

    useEffect(() => {
        setIncrKey(incrKey + 1);
    }, [prepaidObject?.transactions, entitiesList])

    useEffect(() => {
        setIncrKey2(incrKey2 + 1);
    }, [prepaidObject?.topUps])

    const transactionsTableColumns = [
        {
            name: "Αρ.",
            field: "no",
            width: "8%",
            editable: false,
            inputType: {
                type: 'text',
            }
        },
        {
            name: "Ημερομηνία Συναλλαγής",
            field: "dateString",
            editable: false,
            width: "15%",
            inputType: {
                type: 'text',
            }
        },
        {
            name: "Αναγνωριστικά Συναλλαγής",
            field: "identifier",
            editable: true,
            width: "32%",
            inputType: {
                type: "custom-render",
                render: (rowData) => {
                    if (rowData.identifier) {
                        const split = rowData.identifier.split("|");
                        return (
                            <div>{split.map((identifier) => <Badge bg={"primary"} style={{fontSize: "13px", padding: "3px"}} className={"m-1"}>{identifier}</Badge>)}</div>
                        )
                    }
                    return <></>
                }
            }
        },
        {
            name: "Ποσό Συναλλαγής",
            field: "amount",
            editable: true,
            width: "15%",
            inputType: {
                type: "custom-render",
                render: (rowData) => <div className={"text-center"}>{getGreekCurrencyFormat(rowData.amount)}</div>
            }
        },
        {
            name: "Επιλεγμένος προμηθευτής",
            field: "supplierName",
            editable: true,
            width: "30%",
            disabledCallback: (rowData) => rowData?.existent,
            inputType: {
                config: {
                    onChangeSearchCell: (_, keyword, setData) => loadEntitiesList(keyword, setData),
                    excludeColumns: [],
                    columns: [
                        { label: "Κωδικός Συστήματος", name: "systemCode" },
                        { label: "Όνομα", name: "name" },
                        { label: "Τηλέφωνο", name: "phone" },
                        { label: "E-Mail", name: "email" },
                        { label: "Α.Φ.Μ.", name: "vatNumber"},
                    ],
                    data: entitiesList,
                    deleteEnable: false,
                    searchTableEnable: false,
                    showTableOptionOnSelect: true,
                    placeholder: "Επιλέξτε προμηθευτή",
                },
                type: 'advanced-combobox',
            }
        },
    ]

    const topUpsTableColumns = [
        {
            name: "Αρ.",
            field: "no",
            width: "8%",
            editable: false,
            inputType: {
                type: 'text',
            }
        },
        {
            name: "Ημερομηνία Συναλλαγής",
            field: "dateString",
            editable: false,
            width: "15%",
            inputType: {
                type: 'text',
            }
        },
        {
            name: "Αναγνωριστικά Συναλλαγής",
            field: "identifier",
            editable: true,
            width: "32%",
            inputType: {
                type: "custom-render",
                render: (rowData) => {
                    if (rowData.identifier) {
                        const split = rowData.identifier.split("|");
                        return (
                            <div>{split.map((identifier) => <Badge bg={"primary"} style={{fontSize: "13px", padding: "3px"}} className={"m-1"}>{identifier}</Badge>)}</div>
                        )
                    }
                    return <></>
                }
            }
        },
        {
            name: "Ποσό Συναλλαγής",
            field: "amount",
            editable: true,
            width: "15%",
            inputType: {
                type: "custom-render",
                render: (rowData) => <div className={"text-center"}>
                    {getGreekCurrencyFormat(rowData.amount)}
                    {rowData.existent && (
                        <div style={{textAlign: "center"}} className={"mt-1"}><Badge bg={"primary"}>Η εγγραφή της φόρτισης υπάρχει ήδη</Badge></div>
                    )}
                </div>
            }
        },
    ];

    const loadEntitiesList = (keyword, setData) => {
        if (!Array.isArray(setData)) setData = [setData];

        const reqData = { company: company.id }
        if (keyword) reqData.name = keyword;

        axios.get(`${process.env.REACT_APP_API_URL2}/accounting/get-subAccount-by-word`, {
            headers: {"Content-Type": "application/json"},
            params: {
                company: company.id,
                subAccountType: "supplier",
                keyword,
            },
        }).then((res) => {
            if (res.data && res.data.status === "200") {
                if (Array.isArray(setData)) {
                    for (const func of setData) {
                        if (res.data.data) func(res.data.data);
                        else func([]);
                    }
                }
            } else {
                for (const func of setData) func([]);
            }
        }).catch((e) => {
            console.log(e);
        })
    }

    const onTableTransactionsUpdate = {
        insert: () => {},
        update: (data, id, rowIndex, e) => {
            const cloneData = quickClone(prepaidObject.transactions);
            if (data.supplierName) {
                cloneData[rowIndex] = {
                    ...cloneData[rowIndex],
                    supplier: e.row._id,
                    supplierName: e.row.name,
                }
            }
            setPrepaidObject({...prepaidObject, transactions: cloneData});
        },
        selected: () => {},
        delete: () => {}
    }

    const onTableTopUpsUpdate = {
        insert: () => {},
        update: (data, id, rowIndex, e) => {},
        selected: () => {},
        delete: () => {}
    }

    const handleOnChange = (e, type, name) => {
        const cloneIt = quickClone(prepaidObject);
        if (type === "dd") {
            cloneIt[name] = e.value;
        }
        setPrepaidObject(cloneIt);
    }

    const saveAttempt = () => {
        // Quick validations
        if (selectedPaymentTypeType === "pos" && isEmpty(prepaidObject.card)) return toast.error("Παρακαλώ επιλέξτε κάρτα.");
        if (["bank-account", "bank-account-outside", "web-banking"].includes(selectedPaymentTypeType)
            && isEmpty(prepaidObject.bankAccount)) return toast.error("Παρακαλώ επιλέξτε Τραπεζικό Λογαριασμό.");

        // Save
        onSave(aiDoc, prepaidObject, setLoadingSave);
    }

    return (
        <React.Fragment>
            <Modal show={show} onHide={() => setShow(false)} backdrop={"static"} dialogClassName={"modal95PercentWidth"}>
                <Modal.Header closeButton>
                    <Modal.Title>Καταχώρηση Πληρωμών μέσω κάρτας</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {prepaidObject !== null && (
                        <React.Fragment>
                            <Alert variant={"secondary"} style={{fontSize: "16px"}}>
                                Το παρόν αρχείο έχει εντοπιστεί ότι αποτελεί πακέτο εκκαθάρισης συναλλαγών κάρτας της επιχείρησης.
                                Παρακάτω φαίνεται αναλυτικός πίνακας ανά συναλλαγή, καθώς και ο εντοπισμένος προμηθευτής που αντιστοιχεί.
                                Κατά την αποθήκευση δημιουργούνται μόνο οι ανάλογες πληρωμές / επιστροφές πληρωμών καθώς οι λογιστικές
                                εγγραφές φόρτισης καρτών, όπου εντοπίζονται.<br/><br/>
                                <strong>Πλήθος συναλλαγών:</strong> {prepaidObject.totalTransactions}<br/>
                                <strong>Συνολικό ποσό συναλλαγών:</strong> {getGreekCurrencyFormat(prepaidObject.totalAmount)}<br/>
                                {prepaidObject.topUpsCount > 0 && (
                                    <React.Fragment>
                                        <strong>Πλήθος φορτίσεων:</strong> {prepaidObject.topUpsCount}<br/>
                                        <strong>Συνολικό ποσό φορτίσεων:</strong> {getGreekCurrencyFormat(prepaidObject.totalTopUps)}<br/>
                                    </React.Fragment>
                                )}
                            </Alert>
                            <Row>
                                <Col md={3}>
                                    <Dropdown
                                        name={"installation"}
                                        label={"Εγκατάσταση"}
                                        key={Math.random()}
                                        required={true}
                                        options={installationOptions}
                                        defaultValue={installationOptions.find((el) => el.value === prepaidObject.installation)}
                                        onChange={(e) => handleOnChange(e, "dd", "installation")}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Dropdown
                                        name={"paymentType"}
                                        label={"Τρόπος Πληρωμής"}
                                        key={Math.random()}
                                        required={true}
                                        disabled={cardIsLocked}
                                        options={buysPaymentTypes}
                                        defaultValue={buysPaymentTypes.find((el) => el.value === prepaidObject.paymentType)}
                                        onChange={(e) => handleOnChange(e, "dd", "paymentType")}
                                    />
                                </Col>
                                {selectedPaymentTypeType === "pos" ? (
                                    <Col md={3}>
                                        <Dropdown
                                            name={"card"}
                                            label={"Συσχετιζόμενη Κάρτα"}
                                            key={Math.random()}
                                            required={true}
                                            disabled={cardIsLocked}
                                            options={cardOptions}
                                            defaultValue={cardOptions.find((el) => el.value === prepaidObject.card)}
                                            onChange={(e) => handleOnChange(e, "dd", "card")}
                                        />
                                    </Col>
                                ) : ["bank-account", "bank-account-outside", "web-banking"].includes(selectedPaymentTypeType) ? (
                                    <Col md={3}>
                                        <Dropdown
                                            name={"bankAccount"}
                                            label={"Τραπεζικός Λογαριασμός"}
                                            key={Math.random()}
                                            required={true}
                                            options={bankAccountOptions}
                                            defaultValue={bankAccountOptions.find((el) => el.value === prepaidObject.bankAccount)}
                                            onChange={(e) => handleOnChange(e, "dd", "bankAccount")}
                                        />
                                    </Col>
                                ) : (
                                    <React.Fragment></React.Fragment>
                                )}
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Dropdown
                                        name={"documentType"}
                                        label={"Γενικό Παραστατικό"}
                                        key={Math.random()}
                                        required={true}
                                        options={documentTypeOptions}
                                        defaultValue={documentTypeOptions.find((el) => el.value === prepaidObject.documentType)}
                                        onChange={(e) => handleOnChange(e, "dd", "documentType")}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Dropdown
                                        name={"preNumber"}
                                        label={"Αρίθμηση"}
                                        key={Math.random()}
                                        required={true}
                                        options={preNumberGreek}
                                        defaultValue={preNumberGreek.find((el) => el.value === prepaidObject.preNumber)}
                                        onChange={(e) => handleOnChange(e, "dd", "preNumber")}
                                    />
                                </Col>
                                {prepaidObject.transactions.some((el) => el.amount > 0) && (
                                    <React.Fragment>
                                        <Col md={3}>
                                            <Dropdown
                                                name={"documentTypeReturn"}
                                                label={"Γενικό Παραστατικό Επιστροφών"}
                                                key={Math.random()}
                                                required={true}
                                                options={documentTypeOptions}
                                                defaultValue={documentTypeOptions.find((el) => el.value === prepaidObject.documentTypeReturn)}
                                                onChange={(e) => handleOnChange(e, "dd", "documentTypeReturn")}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Dropdown
                                                name={"preNumberReturn"}
                                                label={"Αρίθμηση Επιστροφών"}
                                                key={Math.random()}
                                                required={true}
                                                options={preNumberGreek}
                                                defaultValue={preNumberGreek.find((el) => el.value === prepaidObject.preNumberReturn)}
                                                onChange={(e) => handleOnChange(e, "dd", "preNumberReturn")}
                                            />
                                        </Col>
                                    </React.Fragment>
                                )}
                            </Row>
                            {prepaidObject.transactions.length > 0 && (
                                <Row>
                                    <Col md={12}>
                                        <div className={"text-muted mb-1"}><i>Πίνακας συναλλαγών</i></div>
                                        <EditableTable
                                            tableName={"AI Prepaid Statements Transaction Table"}
                                            key={"AI Prepaid Statements Transaction Table" + incrKey}
                                            allowActions={false}
                                            onUpdate={onTableTransactionsUpdate}
                                            allowInsertRow={false}
                                            columns={transactionsTableColumns}
                                            enableNewRowCheckbox={false}
                                            data={prepaidObject.transactions}
                                        />
                                    </Col>
                                </Row>
                            )}
                            {prepaidObject.topUps.length > 0 && (
                                <Row>
                                    <Col md={12}>
                                        <div className={"text-muted mb-1"}><i>Πίνακας φορτίσεων</i></div>
                                        <EditableTable
                                            tableName={"AI Prepaid Statements Top Ups Table"}
                                            key={"AI Prepaid Statements Top Ups Table" + incrKey2}
                                            allowActions={false}
                                            onUpdate={onTableTopUpsUpdate}
                                            allowInsertRow={false}
                                            columns={topUpsTableColumns}
                                            enableNewRowCheckbox={false}
                                            data={prepaidObject.topUps}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </React.Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-danger" className={"mr-1"} onClick={() => onDecline(aiDoc)}>
                        <MdOutlineCancel size={16} /> Απόρριψη
                    </Button>
                    <Button variant="primary" className={"mr-1"} onClick={() => onReprocess(aiDoc)}>
                        <TbReload size={16} /> Επαναεπεξεργασία
                    </Button>
                    <Button variant="outline-primary" onClick={() => setShow(false)}>
                        Κλείσιμο
                    </Button>
                    <Button variant="primary" onClick={() => saveAttempt()} disabled={loadingSave}>
                        Αποθήκευση {loadingSave && <Spinner className={"ml-2"} animation={"border"} variant={"dark"} />}
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    )
}

export default AIPrepaidStatementModal;
